




















































import FrameBreak from '@/pages/shop/components/FrameBreak.vue';
import Axios from 'axios';
import Vue from 'vue';
import Component from 'vue-class-component';
import VueRouter, { NavigationGuardNext, Route } from 'vue-router';
import { Mutation, State } from 'vuex-class';
import { SET_PAGES } from './store';
import { ShopModuleNotReady } from '@/pages/shop/modules/module';
import {
    EventSelection,
    HttpClient,
    IShopConfig,
    LogLevel,
    LogMessage,
    OpenTicketShop,
    ReservationExpiration,
    ShopFilter,
    State as ShopState,
} from '@openticket/lib-shop';
import {
    hasLocalStorage,
    isIntegrated,
    IsIntegratedResult,
    LocalStorage,
    registerQueueIt,
    registerTurnstile,
} from '../../utils';
import TimeoutModal from './modals/Timeout.vue';
import { STORAGE_PAYMENT_METHOD_KEY } from './modules/Payment.vue';
import Footer from '@/components/Footer.vue';
import { Inject } from 'vue-property-decorator';
import {
    CustomShopSettingsPage,
    CustomShopSettingsStatic,
} from '@openticket/lib-custom-shop-settings';
import defaultPages from './pages.json';
import { BaseInit, OverlayManager } from '@/Base.vue';
import { StyleTheme } from '@openticket/lib-style';
import { NotificationConfig } from '@openticket/vue-notifications';
import ClosedView from './views/Closed.vue';
import Tracking from './Tracking.vue';
import { ShopTweakConfigModule, ShopTweakConfigPage } from './store/types';
import CookieWall from '@/components/cookies/CookieWall.vue';
import { getCookieMessages, initCookies } from '@/pages/shop/cookies';
import { WAITING_LIST_ROUTE_NAME } from '@/utils/waitingList';
import QueueCheck from '@/pages/shop/modals/QueueCheck.vue';
import Turnstile from '@/pages/shop/modals/Turnstile.vue';
import BotProt from '@/pages/shop/modals/BotProt.vue';
import { BotProtClient } from '@/utils/botProt';
import { DEFAULT_DEFERRED_ORDER_API_URL } from '@/composables/deferred';

const DEFAULT_SHOP_API_URL = 'https://shop.api.openticket.tech';
const DEFAULT_BOT_PROT_SERVICE_URL = 'https://botprot.openticket.local';

interface CachedPaymentMethod {
    guid: string;
    issuer?: string;
}

function pageHasAnyModule(
    page: CustomShopSettingsPage,
    module: string,
    ...modules: string[]
): boolean {
    const moduleNames: [string, ...string[]] = [module, ...modules];

    return (
        !!page &&
        page.modules.some((module: string | ShopTweakConfigModule) => {
            return moduleNames.includes(
                typeof module === 'string' ? module : module.name
            );
        })
    );
}

function transformQueryForBackwardsCompatibility(
    $router: VueRouter,
    $route: Route
) {
    // Ugly fix for backwards compatibility
    try {
        if (!$route.query.event && $route.query.eventId) {
            const { eventId } = $route.query;

            const query: Route['query'] = {
                ...$route.query,
                event: eventId,
            };
            delete query.eventId;

            void $router.replace({ query });
        }
    } catch (e) {
        // No-op
    }
}

@Component({
    components: {
        CookieWall,
        FrameBreak,
        ClosedView,
        Tracking,
        Footer,
        TimeoutModal,
        QueueCheck,
        BotProt,
        Turnstile,
    },
})
export default class ShopView extends Vue {
    @Mutation(SET_PAGES)
    setPages!: (pages: ShopTweakConfigPage[]) => void;

    @State('pages')
    pages!: ShopTweakConfigPage[];

    @Inject('overlay')
    overlay!: OverlayManager;

    @Inject('baseInit')
    baseInit!: BaseInit;

    botProtClient: BotProtClient | null = null;

    waiting = false;
    initialized = false;
    initError: LogMessage | null = null;
    preferredEntryPage: string | null = null;
    breakFrame = false;
    showTimeoutModal = false;
    showQueueCheckModal = false;

    triggerCookieWallOpen = 0;
    turnstileSiteKey: string | null =
        '0x4AAAAAACeFejf_fHtjiv0k' ||
        process.env.VUE_APP_TURNSTILE_SITE_KEY ||
        null;
    botProtDisabled = false;

    closeOverlay!: () => void;
    cookiesPromise: null | Promise<void> = null;

    async created(): Promise<void> {
        transformQueryForBackwardsCompatibility(this.$router, this.$route);

        this.closeOverlay = this.overlay.show();

        // Listen for notification events, only show when not embedded
        this.$shop.events.on(
            ['notification'],
            (_path: string[], data: NotificationConfig) => {
                let notification!: NotificationConfig;
                if (data.slug) {
                    if (typeof data.slug === 'string') {
                        if (typeof data.slugCount === 'number') {
                            notification = {
                                type: data.type,
                                message: this.$tc(
                                    data.slug,
                                    data.slugCount,
                                    data.slugData
                                ) as string,
                            };
                        } else {
                            notification = {
                                type: data.type,
                                message: this.$t(
                                    data.slug,
                                    data.slugData
                                ) as string,
                            };
                        }
                    } else {
                        notification = {
                            type: data.type,
                            message: {
                                text:
                                    typeof data.slugCount === 'object' &&
                                    data.slugCount.text
                                        ? (this.$tc(
                                              data.slug.text,
                                              data.slugCount.text,
                                              data.slugData
                                          ) as string)
                                        : (this.$t(
                                              data.slug.text,
                                              data.slugData
                                          ) as string),
                                subText:
                                    typeof data.slugCount === 'object' &&
                                    data.slugCount.subText
                                        ? (this.$tc(
                                              data.slug.subText,
                                              data.slugCount.subText,
                                              data.slugData
                                          ) as string)
                                        : (this.$t(
                                              data.slug.subText,
                                              data.slugData
                                          ) as string),
                            },
                        };
                    }
                } else {
                    if (typeof data.message === 'string') {
                        notification = {
                            type: data.type,
                            message: data.message,
                        };
                    } else {
                        notification = {
                            type: data.type,
                            message: data.message,
                        };
                    }
                }

                if (!this.$shop.isFramed) {
                    this.$notifications.show(notification);
                } else {
                    this.$shop.sendClientNotification(notification);
                }
            }
        );

        this.$shop.events.on(
            ['settings', 'static'],
            (_, settings: CustomShopSettingsStatic) => {
                if (this.$settings) {
                    this.$settings.localUpdateStaticSettings(settings);

                    this.$style.setTheme(
                        this.$settings.static.theme as StyleTheme
                    );

                    if (this.$settings.static.style) {
                        this.$style.setStyle(this.$settings.static.style);
                    }
                }
            }
        );

        if (this.$route.params.page) {
            this.preferredEntryPage = this.$route.params.page;
        }

        const shop_id = this.$route.params.shop_id;

        const baseInitPromise = this.baseInit(shop_id);

        // DD-4E4A - JTI can be used to bypass shop caching on a special api url
        const query = new URLSearchParams(window.location.search);
        const jti = query.get('jti');

        // Create config object with shop id from the URL
        const config: IShopConfig = !jti
            ? this.createConfig(shop_id)
            : this.createConfigNoCache(shop_id, jti);

        if (jti) {
            // If a jti is set: remove it from the url
            const newQuery = { ...this.$route.query };
            delete newQuery['jti'];
            await this.$router.replace({ query: newQuery });
        }

        if (!this.initQueueIt(config)) {
            console.log('QueueIt registration failed');
        }

        if (!this.initTurnstile(config)) {
            console.log('Turnstile registration skipped');
        }

        this.initBotProt(config);

        // Create filters for dates if the shop is a timeslot shop
        const filters: ShopFilter | undefined = this._determineFilters();

        // Start initialization of the shop
        try {
            // Setup the shop structure and fetch the initial shop data
            await this.$shop.init(config, filters);
        } catch (error) {
            this.closeOverlay();

            // Ignore any errors here, they will be handled by _finishInitialization
            this.waiting = this.$shop.state === ShopState.Waiting;

            if (
                error &&
                error._isOpenTicketLogMessage &&
                error.slug === 'sdkshop.log.shop.init.error.fatal' &&
                error.parent &&
                error.parent.slug === 'sdkshop.log.shop.data.error.notfound'
            ) {
                await this.$router.push({
                    name: 'error',
                    query: { redirect: this.$route.path },
                });

                return;
            }

            if (!this.waiting) {
                // TODO Show some kind of error???
                //  as the loading screen is shown in this case...
                //  probably show some overlay like dashboard ???

                await this.$router.push({
                    name: 'error',
                    query: { redirect: this.$route.path },
                });

                throw error;
            }

            return;
        }

        // Set the initial document title early
        // Will already update the page title if the shop is only partially initialized.
        if (this.$shop.data && this.$shop.data.name) {
            document.title = this.$shop.data.name;
        }

        await baseInitPromise;

        if (this.detectFrameBreak()) {
            this.breakFrame = true;

            this.$nextTick(() => {
                this.closeOverlay();
            });

            return;
        }

        this.$localization.on('locale-change', () => {
            this.$cookies.setMessages(getCookieMessages(this));
        });

        this.cookiesPromise = initCookies(
            this.$shop,
            this.$cookies,
            getCookieMessages(this)
        );

        // Set some properties based on the tweaks
        this.initShopPages();

        // Let component initialization finish!
        this.$nextTick(() => {
            this._finishInitialization();
        });
    }

    initQueueIt(config: { httpClient?: HttpClient }): boolean {
        // DD-4E50 - Currently, the validity of the token is not manually checked,
        // nor is whether the token matches the current 'top' waiting room action.
        // This is left up to (1) QueueIt and (2) the shop API.
        return registerQueueIt(
            this.$shop.log,
            process.env.VUE_APP_SHOP_API_URL || DEFAULT_SHOP_API_URL,
            config.httpClient,
            process.env.VUE_APP_DEFERRED_ORDER_API_URL ||
                DEFAULT_DEFERRED_ORDER_API_URL
        );
    }

    initTurnstile(config: { httpClient?: HttpClient }): boolean {
        if (!this.turnstileSiteKey) {
            return false;
        }

        return registerTurnstile(
            this.$shop.log,
            process.env.VUE_APP_SHOP_API_URL || DEFAULT_SHOP_API_URL,
            config.httpClient
        );
    }

    onTurnstileVerified(token: string): void {
        this.$shop.log.create(
            LogLevel.Info,
            ['turnstile', 'verified'],
            { token },
            'Turnstile challenge completed'
        );
    }

    onTurnstileExpired(): void {
        this.$shop.log.create(
            LogLevel.Warning,
            ['turnstile', 'expired'],
            {},
            'Turnstile token expired'
        );
    }

    onTurnstileError(): void {
        this.$shop.log.create(
            LogLevel.Error,
            ['turnstile', 'error'],
            {},
            'Turnstile challenge failed'
        );
    }

    initBotProt(config: {
        baseUrl: string;
        deferredOrderUrl?: string;
        httpClient?: HttpClient;
    }): void {
        this.botProtClient = new BotProtClient({
            httpClient: config.httpClient,
            logger: this.$shop.log,
            shopApiUrl:
                config.baseUrl ||
                process.env.VUE_APP_SHOP_API_URL ||
                DEFAULT_SHOP_API_URL,
            deferredShopApiUrl:
                config.deferredOrderUrl ||
                process.env.VUE_APP_DEFERRED_ORDER_API_URL ||
                DEFAULT_DEFERRED_ORDER_API_URL,
            serviceUrl:
                process.env.VUE_APP_BOT_PROT_SERVICE_URL ||
                DEFAULT_BOT_PROT_SERVICE_URL,
        });
    }

    onBotProtDisabled(): void {
        this.botProtDisabled = true;
    }

    onBotProtVerified({ jwt }: { jwt: string }): void {
        console.log('botprot verified', jwt);

        this.$shop.cart.setBotProtToken(jwt);

        this.$shop.log.create(
            LogLevel.Info,
            ['botprot', 'verified'],
            { jwt },
            'BotProt challenge completed'
        );
    }

    onBotProtExpired(): void {
        this.$shop.log.create(
            LogLevel.Warning,
            ['botprot', 'expired'],
            {},
            'Botprot challenge expired'
        );
    }

    onBotProtError(): void {
        this.$shop.log.create(
            LogLevel.Error,
            ['botprot', 'error'],
            {},
            'Botprot challenge failed'
        );
    }

    // If we are in a deep linked state, and we do not have a first page loaded, we should do so if required
    beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext): void {
        if (
            from.name === WAITING_LIST_ROUTE_NAME &&
            to.name === 'step' &&
            !to.params.page
        ) {
            next({
                name: 'step',
                params: {
                    page: this.computeFirstPage(),
                },
                query: {
                    ...this.$route.query,
                    waitingListEventId: undefined,
                },
            });
            return;
        }
        next();
    }

    async _finishInitialization(): Promise<void> {
        try {
            // Await shop initialization (this will also wait for suspended initialization)
            await this.$shop.initialized;
        } catch (error) {
            this.initError = error;

            this.closeOverlay();

            if (this.$shop.initErrors.length) {
                await this.$router.push({
                    name: 'closed',
                    query: {
                        redirect: this.$route.path,
                    },
                });
            } else {
                await this.$router.push({
                    name: 'error',
                    query: {
                        redirect: this.$route.path,
                    },
                });
            }

            return;
        } finally {
            this.waiting = false;
        }

        this.initError = null;

        this.$shop.cart.setLocale(this.$localization.locale.locale);

        await this._checkCouponParam();

        this._checkShopCodeParam();

        if (this.$shop.data.company.timezone) {
            this.$localization.setTimezone(this.$shop.data.company.timezone);
        }

        // TODO Add shop data locale to localize defaults -> which should trigger a change if it needs to!.

        // (Re-)set the document title
        document.title = this.$shop.data.name || '';
        this.$shop.events.on(
            ['shop', 'locale'],
            async (_path: string[], locale: string) => {
                await this.$localization.setLocale(locale);
            }
        );

        this.$shop.events.on(
            ['timer', 'clear'],
            async (
                path: string[],
                data: ReservationExpiration & { timeout: boolean }
            ) => {
                if (!data.timeout) {
                    return;
                }

                await this.pushFirstPage();
                this.showTimeoutModal = true;
            }
        );

        await this.useLastUsedPaymentMethod();

        try {
            await this.pushFirstPage();
        } finally {
            this.initialized = true;
        }

        if (this.$route.query.sessionId) {
            this.$router.replace({
                path: this.$route.path,
                query: {
                    ...this.$route.query,
                    sessionId: undefined,
                },
            });
        }

        this.$nextTick(() => {
            this.closeOverlay();
        });

        await this.cookiesPromise;

        this.$nextTick(() => {
            void (async () => {
                const isIntegratedResult: IsIntegratedResult = await isIntegrated(
                    this.$shop.messaging
                );

                if (!isIntegratedResult.integrated) {
                    return;
                }

                this.$shop.log.create(LogLevel.Info, ['integrated'], {
                    integrator_type: isIntegratedResult.type,
                    integrator_version: isIntegratedResult.version,
                    referrer: getReferrer(),
                });
            })();
        });

        if (this.$route.query.queueittoken) {
            // If a queueittoken is set: remove it from the url
            const newQuery = { ...this.$route.query };
            delete newQuery['queueittoken'];
            await this.$router.replace({ query: newQuery });
        }
    }

    /**
     *  Used to check if a coupon param has been set, and add the coupon if so.
     */
    async _checkCouponParam(): Promise<void> {
        const coupons: string[] = ([] as Array<string | null>)
            .concat(this.$route.query.coupon) // Force array
            .filter<string>((v: string | null): v is string => !!v) // Truthy
            .filter((v, i, arr) => arr.indexOf(v) === i); // Unique

        for (const coupon of coupons) {
            try {
                await this.$shop.cart.addCoupon(coupon);
            } catch {
                // Fail silently when the coupon seems invalid
            }
        }
    }

    /**
     *  Used to check if the shop_code param has been set, and
     *  set it as tech data in the shop (SDK) if so.
     */
    _checkShopCodeParam(): void {
        const shopCodes: string[] = ([] as Array<string | null>)
            .concat(this.$route.query.shop_code) // Force array
            .filter<string>((v: string | null): v is string => !!v) // Truthy
            .filter((v, i, arr) => arr.indexOf(v) === i); // Unique

        if (!shopCodes.length) {
            return;
        }

        this.$shop.cart.setTechData({
            shop_code: shopCodes[0],
            shop_codes: shopCodes,
        });
    }

    async pushFirstPage(): Promise<Route | void> {
        // If deeplinking into waitinglist we do not want to push first page
        if (this.$route.name === WAITING_LIST_ROUTE_NAME) {
            return;
        }

        try {
            return await this.$router.replace({
                name: 'step',
                params: {
                    page: this.computeFirstPage(),
                },
                query: this.$route.query,
            });
        } catch (e) {
            if (
                !e.from ||
                !e.to ||
                e.from.name !== e.to.name ||
                !e.from.params ||
                !e.to.params ||
                e.from.params.page !== e.to.params.page
            ) {
                throw e;
            }

            // Ignore navigation duplicated error if the page is the same.
        }
    }

    computeFirstPage(): string {
        const defaultPageName: string = this.pages[0].name;

        try {
            if (!this.preferredEntryPage) {
                return defaultPageName;
            }

            const preferredPageIndex: number = this.pages.findIndex(
                (page: ShopTweakConfigPage) =>
                    page.name === this.preferredEntryPage
            );

            if (preferredPageIndex < 0) {
                return defaultPageName;
            }

            const previousPages: ShopTweakConfigPage[] = this.pages.slice(
                0,
                preferredPageIndex
            );

            let currentPageName: string = defaultPageName;

            // Currently, only the events-module is skippable.
            // Other modules require validation, which is currently a stateful operation.
            // Once this is a stateless (or at least does not require initialized and mounted components)
            // this could be changed to just allow all modules to be skipped,
            // as long as they validate successfully
            const skippableModules: string[] = ['events'];

            // Test every module for every page
            // It should both ready and valid, if a module is not.
            // That page will become the first page.
            // Even if a next page is desired.
            for (const previousPage of previousPages) {
                if (!previousPage.modules) {
                    // This is a weird edge case. if there are no modules on a page,
                    // it is undefined what behaviour should happen.
                    // Therefore, just let the user walk through the pages from the page before this.
                    return currentPageName;
                }

                currentPageName = previousPage.name;

                for (const module of previousPage.modules) {
                    const moduleName: string =
                        typeof module === 'string' ? module : module.name;

                    // Events module (or the next page/modules) do not validate if an event was actually chosen if it had to.
                    // So this is a custom check for the entry point
                    if (moduleName === 'events' && !this.$route.query.event) {
                        return currentPageName;
                    }

                    if (!skippableModules.includes(moduleName)) {
                        return currentPageName;
                    }

                    const moduleComponent:
                        | undefined
                        | {
                              isReady?: (
                                  shop: OpenTicketShop
                              ) => ShopModuleNotReady | null;
                          } =
                        // @ts-expect-error - options is where globally registered components are held.
                        Vue.options.components[`shop-module-${moduleName}`];

                    if (!moduleComponent || !moduleComponent.isReady) {
                        return currentPageName;
                    }

                    const notReadyError: ShopModuleNotReady | null = moduleComponent.isReady(
                        this.$shop
                    );

                    if (notReadyError) {
                        return currentPageName;
                    }
                }
            }

            return this.preferredEntryPage;
        } catch (e) {
            console.error(e);

            return defaultPageName;
        }
    }

    _determineFilters(): ShopFilter | undefined {
        const query = this.$route.query;

        const filters = ['start', 'end'];

        const map: ShopFilter = {};

        for (const filter of filters) {
            if (query[filter]) {
                map[filter] = query[filter] as string;
            }
        }

        if (Object.keys(map).length < 1) {
            return undefined;
        }

        return map;
    }

    // noinspection JSMethodCanBeStatic
    /**
     * Create the basic shop config object
     */
    createConfig(shopId: string): IShopConfig {
        return {
            baseUrl: process.env.VUE_APP_SHOP_API_URL || DEFAULT_SHOP_API_URL,
            deferredOrderUrl:
                process.env.VUE_APP_DEFERRED_ORDER_API_URL ||
                DEFAULT_DEFERRED_ORDER_API_URL,
            guid: shopId,
            httpClient: Axios.create(),
            logLevels: {
                console: LogLevel.Debug,
                service: LogLevel.Info,
            },
            cseUrls: {
                adyenPlatformCardUrl: process.env.VUE_APP_ADYEN_PLATFORM_CC_URL,
                externalCardFieldsUrl:
                    process.env.VUE_APP_EXTERNAL_CARD_FIELDS_URL,
                surchargeUrl: process.env.VUE_APP_SURCHARGE_URL,
            },
            reservator: {
                maxConcurrentReservations: 1,
                maxPendingReservations: 1,
                minRequestTime: 350,
            },
            loggerUrl: process.env.VUE_APP_LOGGER_URL,
        };
    }

    /**
     * Create the basic shop config object and inject the cacheless base url + jti
     * (If both jti and VUE_APP_SHOP_API_URL_NO_CACHE are set and sane)
     * @param shopId
     * @param jti
     */
    createConfigNoCache(shopId: string, jti: string): IShopConfig {
        const config = this.createConfig(shopId);

        // JTI length is normally 80 characters
        if (
            jti.length === 80 &&
            process.env.VUE_APP_SHOP_API_URL_NO_CACHE &&
            process.env.VUE_APP_SHOP_API_URL_NO_CACHE.length > 0
        ) {
            // Only use if everything is sane
            config.baseUrl = process.env.VUE_APP_SHOP_API_URL_NO_CACHE.replace(
                /\/$/,
                ''
            );
            config.jti = jti;
        }
        return config;
    }

    /**
     * Determine if a frame break is needed
     */
    detectFrameBreak(): boolean {
        if (!this.$settings || !this.$settings.static.shop.blockFrameAccess) {
            return false;
        }

        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    /**
     * Create and initialize the shop tweak data
     */
    initShopPages(): void {
        let pages!: CustomShopSettingsPage[];

        // Check if ShopSettingsClient exists
        if (
            !this.$settings ||
            !this.$settings.dynamic ||
            !this.$settings.dynamic.shop ||
            !this.$settings.dynamic.shop.pages
        ) {
            // If not, get the default pages
            pages = this.transformShopPages();
        } else {
            // Get pages from custom settings
            pages = this.transformShopPages(this.$settings.dynamic.shop.pages);
        }

        // Set the pages in the vuex instance
        this.setPages(pages);
    }

    /**
     * Transform the pages provided by the tweaks to a more consistent format
     */
    transformShopPages(
        customPages?: CustomShopSettingsPage[]
    ): CustomShopSettingsPage[] {
        const pages: CustomShopSettingsPage[] = customPages || defaultPages;

        // Loop over all pages to transform all modules with type string to
        // objects containing the name property
        for (const page of pages) {
            const modules = page.modules || [];

            for (let i = 0; i < modules.length; i++) {
                if (typeof modules[i] === 'string') {
                    modules[i] = {
                        name: modules[i] as string,
                    };
                }
            }

            page.modules = modules;
        }

        // Add an events page if the shop is not a timeslot shop
        // Changing this can cause unintended side-effects in the timeslot placement
        if (!this.$shop.data.greedy_date_selection && this.hasEventSelection) {
            const eventPage: ShopTweakConfigPage & CustomShopSettingsPage = {
                name: 'events',
                title: 'shop.pages.shop.events',
                hideOrderBar: true,
                modules: [
                    {
                        name: 'events',
                    },
                ],
            };

            if (
                pages[0] &&
                pageHasAnyModule(pages[0], 'external-validation-form') &&
                !pageHasAnyModule(pages[0], 'tickets', 'products')
            ) {
                // Note, if the external-validation-form results in a filtered shop,
                // which would have changed constraints for the event selection boolean,
                // this is currently NOT taken into account.
                // i.e. When the event selection is set to auto and after the external validation
                // form the number of events is less than 4.
                return [pages[0], eventPage, ...pages.slice(1)];
            }

            return [eventPage, ...pages];
        }

        return pages;
    }

    get hasEventSelection(): boolean {
        switch (this.$shop.data.event_selection) {
            case EventSelection.Enabled:
                return true;

            case EventSelection.Disabled:
                return false;

            default:
                return (
                    this.$shop.data.events.length > 3 &&
                    !this.$shop.data.greedy_date_selection
                );
        }
    }

    /**
     *  If a payment method was used before,
     *  it is applied to the cart.
     *
     *  Cleans up the cached value if an exception occurs.
     */
    async useLastUsedPaymentMethod(): Promise<void> {
        if (!hasLocalStorage()) {
            return;
        }

        try {
            const cachedMethod: CachedPaymentMethod | null = LocalStorage.get<
                CachedPaymentMethod
            >(STORAGE_PAYMENT_METHOD_KEY);

            if (!cachedMethod) {
                return;
            }

            if (cachedMethod.guid in this.$shop.data.payment_methods_map) {
                await this.$shop.cart.setPaymentMethod(
                    cachedMethod.guid,
                    cachedMethod.issuer
                );
            }
        } catch (e) {
            LocalStorage.remove(STORAGE_PAYMENT_METHOD_KEY);
        }
    }

    get showPoweredBy(): boolean {
        return (
            !this.$settings ||
            !this.$settings.static ||
            !this.$settings.static.shop ||
            !this.$settings.static.shop.footer ||
            this.$settings.static.shop.footer.showPoweredBy ||
            this.$settings.static.shop.footer.showPoweredBy === null
        );
    }

    get showLocaleSwitch(): boolean {
        return (
            !this.$settings ||
            !this.$settings.static ||
            !this.$settings.static.shop ||
            !this.$settings.static.shop.footer ||
            this.$settings.static.shop.footer.showLocaleSwitcher ||
            this.$settings.static.shop.footer.showLocaleSwitcher === null
        );
    }
}

function getReferrer(): string {
    try {
        return (
            window.document.referrer ||
            window.top?.location.toString() ||
            '<hidden>'
        );
    } catch (e) {
        return '<hidden>';
    }
}
